<template>
	<transition
		v-if="campaignEnabled"
		enter-active-class="transition-all duration-300 ease-out"
		enter-from-class="opacity-0"
		enter-to-class="opacity-100"
		leave-active-class="transition-all duration-200 ease-in"
		leave-from-class="opacity-100"
		leave-to-class="opacity-0"
	>
		<div
			v-if="visible"
			class="fixed inset-0 z-[110] print:hidden flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
			role="dialog"
			aria-modal="true"
			aria-label="Free Worship Together starter kit"
			@click.self="dismiss"
		>
			<transition
				enter-active-class="transition-all duration-400 ease-out"
				enter-from-class="opacity-0 scale-95 translate-y-4"
				enter-to-class="opacity-100 scale-100 translate-y-0"
				appear
			>
				<div
					v-if="visible"
					class="relative w-full max-w-[28rem] rounded-2xl overflow-hidden shadow-2xl bg-[#2f3f41] text-white ring-1 ring-black/30"
				>
					<div class="absolute inset-0 opacity-35 pointer-events-none">
						<!-- Site-wide: must not collide with any page banner illustration.
						     This modal is near-square (1.27:1 desktop, 0.93:1 mobile), so a wide
						     landscape source object-cover's into an enormous cropped head with the
						     body gone -- wt-art-congregation (2.25:1) lost 44% of its width here.
						     wt-band-invitation is cut 1.10:1, between the two container ratios, so
						     each viewport trims a few percent off one axis and every face survives. -->
						<img src="/images/wt-band-invitation.jpg" class="w-full h-full object-cover object-center" alt="" />
					</div>
					<div class="absolute inset-0 opacity-30 mix-blend-screen pointer-events-none"
						style="background-image:
							radial-gradient(circle at 18% 25%, rgba(78,111,112,0.55), transparent 55%),
							radial-gradient(circle at 82% 78%, rgba(220,174,99,0.40), transparent 55%);" />
					<div class="absolute inset-0 bg-gradient-to-b from-[#2f3f41]/60 via-[#2f3f41]/85 to-[#2f3f41] pointer-events-none" />

					<button
						@click="dismiss"
						class="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 flex items-center justify-center text-white/80 hover:text-white transition-colors"
						aria-label="Dismiss"
					>
						<i class="fas fa-times text-sm" />
					</button>

					<div class="relative px-6 pt-8 pb-6 text-center">
						<img
							src="/images/wt-brand-logo.png"
							alt=""
							class="w-16 h-16 mx-auto object-contain drop-shadow-lg"
						/>

						<div class="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#dcae63] mt-4">
							Wait — before you go
						</div>
						<div class="font-serif font-bold text-2xl leading-tight mt-3">
							Worship together as a family.
						</div>
						<div class="font-serif italic text-base text-white/80 mt-3 leading-snug max-w-[22rem] mx-auto">
							Take the free &ldquo;Worship Together&rdquo; Starter Kit and begin this Sunday.
						</div>

						<a
							:href="kitUrl"
							@click="trackClick"
							class="mt-6 inline-flex items-center justify-center gap-2 px-8 py-3.5 rounded-full bg-[#cc9d4f] text-white text-sm font-semibold tracking-wide shadow-lg hover:bg-[#b98b44] hover:shadow-xl transition-all group"
						>
							Send me the free kit
							<i class="fas fa-arrow-right text-xs transition-transform group-hover:translate-x-0.5" />
						</a>

						<button
							@click="dismiss"
							class="block w-full mt-4 text-xs text-white/50 hover:text-white/80 tracking-wide transition-colors"
						>
							No thanks
						</button>
					</div>
				</div>
			</transition>
		</div>
	</transition>
</template>

<script>
import createCampaignElementMixin from '../../helpers/campaignElementMixin'
import { shouldSuppressPopup, markDismissed, anyPromoPopupShown, markPromoPopupShown } from '../../helpers/campaignSuppress'

const KIT_PATH = '/worship-together'
const UTM = 'utm_source=cfl-site&utm_medium=exit-intent&utm_campaign=worship-together'
const DISMISS_KEY = 'fwKitExitDismissed'
const SHOWN_KEY = 'fwKitExitShown'
const CONVERTED_KEY = 'fwKitConverted'
const ARM_DELAY_MS = 5_000
const TOP_THRESHOLD_PX = 8
const EXCLUDED_PATTERNS = [/^\/(es|cn)(\/|$|\?)/i, /^\/worship-together(\/|$|\?)/i, /^\/restore-family-worship(\/|$|\?)/i, /checkout/i, /confirmation/i, /starter-kit/i, /thank/i]

export default {
	mixins: [createCampaignElementMixin('exit-intent-popup')],
	inject: ['breakpoints'],
	data() {
		return {
			visible: false,
			armed: false,
		}
	},
	computed: {
		kitUrl() {
			return `${KIT_PATH}?${UTM}`
		},
		onExcludedRoute() {
			const path = this.$route?.path || ''
			return EXCLUDED_PATTERNS.some(re => re.test(path))
		},
		isDesktop() {
			return !!this.breakpoints?.md
		},
	},
	mounted() {
		// Admin artwork preview: show immediately, skip the arm/exit-intent gate.
		if (this.preview) {
			this.visible = true
			return
		}
		if (!this.isDesktop) return
		// Don't arm if dismissed in the past (persisted) or already signed up.
		if (shouldSuppressPopup(DISMISS_KEY)) return
		try {
			if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
		} catch {}

		this._armTimer = window.setTimeout(() => {
			this.armed = true
			this._onMouseOut = this.handleMouseOut.bind(this)
			document.addEventListener('mouseout', this._onMouseOut)
		}, ARM_DELAY_MS)
	},
	beforeUnmount() {
		if (this._armTimer) window.clearTimeout(this._armTimer)
		if (this._onMouseOut) document.removeEventListener('mouseout', this._onMouseOut)
	},
	watch: {
		visible(v) {
			if (v) document.body.style.overflow = 'hidden'
			else document.body.style.overflow = ''
		},
	},
	methods: {
		handleMouseOut(e) {
			if (!this.armed) return
			if (this.visible) return
			if (this.onExcludedRoute) return
			// Only fire when cursor truly leaves the document toward the top
			if (e.relatedTarget || e.toElement) return
			if (e.clientY > TOP_THRESHOLD_PX) return
			this.maybeShow()
		},
		maybeShow() {
			// A pop-up that is switched OFF must not consume the one-promo-per-session slot.
			// Without this, a disabled pop-up still fired its timer, called markPromoPopupShown()
			// and silently suppressed a pop-up the admin had switched ON (HAP-3654). Checked here
			// rather than in mounted() because the flags load asynchronously — at mount time this
			// still holds the offline fallback value.
			if (!this.campaignEnabled) return
			if (this.visible) return
			if (this.onExcludedRoute) return
			// Re-check at show time — signup/dismiss may have happened since arming.
			if (shouldSuppressPopup(DISMISS_KEY)) return
			// Only one promo pop-up per session — if the book-offer modal (or the
			// slide-in) already fired, stand down.
			if (anyPromoPopupShown()) return
			try {
				if (window.sessionStorage.getItem(SHOWN_KEY) === '1') return
				window.sessionStorage.setItem(SHOWN_KEY, '1')
			} catch {}
			this.visible = true
			markPromoPopupShown()
			this.trackImpression()
		},
		dismiss() {
			this.visible = false
			markDismissed(DISMISS_KEY)
			this.trackCampaignDismiss()
		},
		trackImpression() {
			this.trackCampaignImpression()
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_exit_impression', {
						event_category: 'engagement',
						event_label: 'exit-intent-popup',
						route: this.$route?.path,
					})
				}
			} catch {}
		},
		trackClick() {
			this.trackCampaignClick({ link_url: this.kitUrl })
			try {
				if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
					window.gtag('event', 'family_worship_kit_exit_click', {
						event_category: 'engagement',
						event_label: 'exit-intent-popup',
						link_url: this.kitUrl,
						route: this.$route?.path,
					})
				}
			} catch {}
			try {
				window.sessionStorage.setItem(DISMISS_KEY, '1')
				window.sessionStorage.setItem(CONVERTED_KEY, '1')
			} catch {}
		},
	},
}
</script>
